import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GoBackButton from "components/project/GoBackButton";
import AdminFilterBar from "components/admin/AdminFilterBar";
import { getAdminLogs } from "lib/api/getAdminLogs";
import { formatKoreanDateTime } from "lib/utils/koreanDateTime";
import { formatSystemRole } from "lib/utils/roleLabels";

const PAGE_SIZE = 30;

const DEFAULT_QUERY = {
  keyword: "",
  searchField: "all",
  actionFilter: "all",
  startDate: null,
  endDate: null,
};

const SEARCH_FIELD_OPTIONS = [
  { value: "all", label: "통합 검색" },
  { value: "user", label: "사용자" },
  { value: "action", label: "활동" },
  { value: "message", label: "로그 내용" },
];

function formatLogDateTime(value) {
  return formatKoreanDateTime(value, {
    sourceTimeZone: "kst",
    year: "numeric",
  });
}

export default function AdminLogsPage() {
  const [keyword, setKeyword] = useState(DEFAULT_QUERY.keyword);
  const [searchField, setSearchField] = useState(DEFAULT_QUERY.searchField);
  const [actionFilter, setActionFilter] = useState(DEFAULT_QUERY.actionFilter);
  const [startDate, setStartDate] = useState(DEFAULT_QUERY.startDate);
  const [endDate, setEndDate] = useState(DEFAULT_QUERY.endDate);

  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [appendErrorMessage, setAppendErrorMessage] = useState("");
  const [openRowKey, setOpenRowKey] = useState(null);

  const querySeqRef = useRef(0);
  const appendLockRef = useRef(false);
  const sentinelRef = useRef(null);

  const actionOptions = useMemo(() => {
    const values = [
      ...new Set(rows.map((r) => String(r.api_title ?? "").trim())),
    ]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const selected = String(actionFilter ?? "").trim();
    if (selected && selected !== "all" && !values.includes(selected)) {
      values.unshift(selected);
    }

    return [
      { value: "all", label: "전체 활동" },
      ...values.map((v) => ({ value: v, label: v })),
    ];
  }, [rows, actionFilter]);

  const searchPlaceholder = useMemo(() => {
    if (searchField === "user") return "사용자명으로 검색";
    if (searchField === "action") return "활동 검색";
    if (searchField === "message") return "로그 내용으로 검색";
    return "사용자·활동·로그 내용 검색";
  }, [searchField]);

  useEffect(() => {
    const seq = ++querySeqRef.current;
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setIsLoadingMore(false);
      setErrorMessage("");
      setAppendErrorMessage("");
      setRows([]);
      setTotalCount(0);
      setHasMore(false);
      setNextCursor(null);
      appendLockRef.current = false;

      try {
        const result = await getAdminLogs({
          ...query,
          limit: PAGE_SIZE,
          cursorCreatedAt: null,
          cursorId: null,
        });

        if (cancelled || seq !== querySeqRef.current) return;
        if (!result?.success) {
          throw new Error(result?.message || "로그 목록을 불러오지 못했습니다.");
        }

        setRows(Array.isArray(result?.rows) ? result.rows : []);
        setTotalCount(Number(result?.totalCount ?? 0));
        setHasMore(Boolean(result?.hasMore));
        setNextCursor(result?.nextCursor ?? null);
      } catch (error) {
        if (cancelled || seq !== querySeqRef.current) return;
        setRows([]);
        setTotalCount(0);
        setHasMore(false);
        setNextCursor(null);
        setErrorMessage(error?.message || "로그 목록을 불러오지 못했습니다.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [query]);

  const loadNextPage = useCallback(async () => {
    if (
      appendLockRef.current ||
      isLoading ||
      isLoadingMore ||
      !hasMore ||
      !nextCursor?.createdAt ||
      !nextCursor?.id
    ) {
      return;
    }

    appendLockRef.current = true;
    const seq = querySeqRef.current;
    setIsLoadingMore(true);
    setAppendErrorMessage("");

    try {
      const result = await getAdminLogs({
        ...query,
        limit: PAGE_SIZE,
        cursorCreatedAt: nextCursor.createdAt,
        cursorId: nextCursor.id,
      });

      if (seq !== querySeqRef.current) return;
      if (!result?.success) {
        throw new Error(result?.message || "추가 로그를 불러오지 못했습니다.");
      }

      const nextRows = Array.isArray(result?.rows) ? result.rows : [];
      setRows((prev) => [...prev, ...nextRows]);
      setTotalCount(Number(result?.totalCount ?? 0));
      setHasMore(Boolean(result?.hasMore));
      setNextCursor(result?.nextCursor ?? null);
    } catch (error) {
      if (seq !== querySeqRef.current) return;
      setAppendErrorMessage(
        error?.message || "추가 로그를 불러오는 중 오류가 발생했습니다.",
      );
    } finally {
      if (seq === querySeqRef.current) {
        setIsLoadingMore(false);
      }
      appendLockRef.current = false;
    }
  }, [query, isLoading, isLoadingMore, hasMore, nextCursor]);

  useEffect(() => {
    if (isLoading || !hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNextPage();
        }
      },
      {
        root: null,
        rootMargin: "260px 0px",
        threshold: 0,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadNextPage, hasMore, isLoading, rows.length]);

  const submitSearch = (e) => {
    e.preventDefault();
    setQuery({
      keyword,
      searchField,
      actionFilter,
      startDate,
      endDate,
    });
    setOpenRowKey(null);
  };

  const clearAllFilters = () => {
    setKeyword(DEFAULT_QUERY.keyword);
    setSearchField(DEFAULT_QUERY.searchField);
    setActionFilter(DEFAULT_QUERY.actionFilter);
    setStartDate(DEFAULT_QUERY.startDate);
    setEndDate(DEFAULT_QUERY.endDate);
    setQuery(DEFAULT_QUERY);
    setOpenRowKey(null);
  };

  const toggleRow = (key) => {
    setOpenRowKey((prev) => (prev === key ? null : key));
  };

  return (
    <section className="content admin adminDetailPage kpiLogDetail">
      <div className="helpTitleRow">
        <GoBackButton index />
        <h2>로그 상세 조회</h2>
      </div>

      <AdminFilterBar
        keyword={keyword}
        onChangeKeyword={setKeyword}
        keywordPlaceholder={searchPlaceholder}
        searchField={searchField}
        onChangeSearchField={setSearchField}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        extraFilters={[
          {
            key: "actionFilter",
            value: actionFilter,
            onChange: setActionFilter,
            options: actionOptions,
          },
        ]}
        startDate={startDate}
        endDate={endDate}
        onChangeDateRange={(start, end) => {
          setStartDate(start);
          setEndDate(end);
        }}
        onSubmit={submitSearch}
        onResetAll={clearAllFilters}
        isLoading={isLoading}
        resultCount={totalCount}
        resultUnit="건"
      />

      <div className="card detailTableWrap logTableCard">
        <table className="detailTable logKpiTable">
          <thead>
            <tr>
              <th>시간</th>
              <th>사용자</th>
              <th>활동</th>
              <th>로그 요약</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && errorMessage ? (
              <tr>
                <td colSpan={4} className="emptyCell">
                  {errorMessage}
                </td>
              </tr>
            ) : rows.length === 0 && isLoading ? (
              <tr>
                <td colSpan={4} className="emptyCell">
                  로그 목록을 불러오는 중입니다.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="emptyCell">
                  조건에 맞는 로그가 없습니다.
                </td>
              </tr>
            ) : (
              <>
                {rows.map((row, idx) => {
                  const key =
                    row.log_id ??
                    `${row.user_name ?? "u"}-${row.log_created_at ?? "t"}-${idx}`;
                  const isOpen = openRowKey === key;

                  return (
                    <Fragment key={key}>
                      <tr
                        className={`logRow ${isOpen ? "open" : ""}`}
                        onClick={() => toggleRow(key)}
                      >
                        <td>{formatLogDateTime(row.log_created_at)}</td>
                        <td>{row.user_name ?? "-"}</td>
                        <td>{row.api_title ?? "-"}</td>
                        <td>
                          <div className="logPreview">{row.log_message ?? "-"}</div>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="logDetailRow">
                          <td colSpan={4}>
                            <div className="logDetailPanel">
                              <p className="detailMeta">
                                <strong>사용자:</strong> {row.user_name ?? "-"} |{" "}
                                <strong>권한:</strong>{" "}
                                {formatSystemRole(row.user_role)} |{" "}
                                <strong>활동:</strong> {row.api_title ?? "-"} |{" "}
                                <strong>시간:</strong>{" "}
                                {formatLogDateTime(row.log_created_at)}
                              </p>
                              <pre className="detailBody">
                                {row.log_message ?? "-"}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}

                {appendErrorMessage ? (
                  <tr>
                    <td colSpan={4} className="emptyCell">
                      {appendErrorMessage}
                    </td>
                  </tr>
                ) : null}

                {isLoadingMore ? (
                  <tr>
                    <td colSpan={4} className="emptyCell">
                      추가 로그를 불러오는 중입니다.
                    </td>
                  </tr>
                ) : null}

                {!isLoadingMore && hasMore ? (
                  <tr className="logSentinelRow">
                    <td colSpan={4}>
                      <div ref={sentinelRef} className="logLoadSentinel">
                        스크롤하면 다음 로그를 자동으로 불러옵니다.
                      </div>
                    </td>
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
